/**
 * LexiStar usage & billing API client (ST-00207 stream A; docs/lexistar/contracts/
 * m4b-contracts.md §2.4). Server: server/modules/lexistar/billing, mounted at
 * /api/lexistar/billing. Shapes: `types/lexistar-billing`.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response
 * BODY. `tenantId` is for global admins (their own organisation when omitted); for everyone
 * else the server answers for their own organisation and refuses another.
 *
 *   getPlan(tenantId?)                 GET  /plan              → { plan, can_manage }
 *   savePlan(input)                    PUT  /plan              → { plan, changed }   (global admin)
 *   listStatements({from,to,tenantId}) GET  /statements        → StatementSummary[]
 *   getStatement(id)                   GET  /statements/:id    → UsageStatement
 *   downloadStatementPdf(id)           GET  /statements/:id/pdf  (saves the file)
 *   finaliseStatement(id, early?)      POST /statements/:id:finalise → FinaliseResult (global admin)
 *   rebuildMonth(month, tenantId?)     POST /statements:rebuild?month → RebuildResult (global admin)
 *   getAssetDays(month, tenantId?)     GET  /asset-days        → AssetDaysResponse
 *   getUsageSummary(month, tenantId?)  GET  /usage/summary     → UsageSummary
 *   getSubscription(tenantId?)         GET  /subscription      → SubscriptionResponse (ST-00241)
 *   recordNotice(assetId, input)       POST /subscription/:assetId/notices → RemovalNotice (global admin)
 *   withdrawNotice(id, reason)         POST /notices/:id:withdraw → RemovalNotice (global admin)
 *   listTenants()                      GET  /tenants/current/all (global admin) → TenantOption[]
 */
import api from './api';
import type {
  AssetDaysResponse,
  BillingPlan,
  BillingPlanInput,
  FinaliseResult,
  RebuildResult,
  RemovalNotice,
  RemovalNoticeInput,
  StatementSummary,
  SubscriptionResponse,
  TenantOption,
  UsageStatement,
  UsageSummary,
} from '../types/lexistar-billing';

const BASE = '/lexistar/billing';
const enc = encodeURIComponent;

type Params = Record<string, string | number | undefined | null>;
const clean = (params: Params): Record<string, string | number> =>
  Object.fromEntries(
    Object.entries(params).filter((e): e is [string, string | number] => e[1] !== undefined && e[1] !== null && e[1] !== '')
  );

export async function getPlan(tenantId?: number | null): Promise<{ plan: BillingPlan; can_manage: boolean }> {
  const res = await api.get(`${BASE}/plan`, { params: clean({ tenant_id: tenantId }) });
  return res.data as { plan: BillingPlan; can_manage: boolean };
}

export async function savePlan(input: BillingPlanInput): Promise<{ plan: BillingPlan; changed: boolean }> {
  const res = await api.put(`${BASE}/plan`, input);
  return res.data as { plan: BillingPlan; changed: boolean };
}

export async function listStatements(q: { from?: string; to?: string; tenantId?: number | null } = {}): Promise<StatementSummary[]> {
  const res = await api.get(`${BASE}/statements`, { params: clean({ from: q.from, to: q.to, tenant_id: q.tenantId }) });
  const body = res.data as { statements?: StatementSummary[] };
  return Array.isArray(body?.statements) ? body.statements : [];
}

export async function getStatement(id: string): Promise<UsageStatement> {
  const res = await api.get(`${BASE}/statements/${enc(id)}`);
  return (res.data as { statement: UsageStatement }).statement;
}

export async function finaliseStatement(id: string, early = false): Promise<FinaliseResult> {
  const res = await api.post(`${BASE}/statements/${enc(id)}:finalise`, { early }, { timeout: 180000 });
  return res.data as FinaliseResult;
}

export async function rebuildMonth(month: string, tenantId?: number | null): Promise<RebuildResult> {
  const res = await api.post(`${BASE}/statements:rebuild`, {}, { params: clean({ month, tenant_id: tenantId }), timeout: 180000 });
  return res.data as RebuildResult;
}

export async function getAssetDays(month: string, tenantId?: number | null): Promise<AssetDaysResponse> {
  const res = await api.get(`${BASE}/asset-days`, { params: clean({ month, tenant_id: tenantId }) });
  return res.data as AssetDaysResponse;
}

export async function getUsageSummary(month: string, tenantId?: number | null): Promise<UsageSummary> {
  const res = await api.get(`${BASE}/usage/summary`, { params: clean({ month, tenant_id: tenantId }) });
  return res.data as UsageSummary;
}

/** The trucks on the subscription today, their activation and written removal notices (ST-00241). */
export async function getSubscription(tenantId?: number | null): Promise<SubscriptionResponse> {
  const res = await api.get(`${BASE}/subscription`, { params: clean({ tenant_id: tenantId }) });
  return res.data as SubscriptionResponse;
}

/** Record a written removal notice for a truck (global admin). */
export async function recordNotice(assetId: string, input: RemovalNoticeInput): Promise<RemovalNotice> {
  const res = await api.post(`${BASE}/subscription/${enc(assetId)}/notices`, input);
  return (res.data as { notice: RemovalNotice }).notice;
}

/** Withdraw a removal notice before its billing end date (global admin). */
export async function withdrawNotice(id: string, reason: string): Promise<RemovalNotice> {
  const res = await api.post(`${BASE}/notices/${enc(id)}:withdraw`, { reason });
  return (res.data as { notice: RemovalNotice }).notice;
}

/** The organisations a global admin may pick (GET /tenants/current/all). */
export async function listTenants(): Promise<TenantOption[]> {
  const res = await api.get('/tenants/current/all');
  const body = res.data as { data?: Array<{ tenant_id: number; tenant_name: string }> };
  return (Array.isArray(body?.data) ? body.data : []).map((t) => ({ tenant_id: Number(t.tenant_id), tenant_name: String(t.tenant_name) }));
}

/** The file name the server gave (Content-Disposition), else `fallback`. */
export function filenameOf(disposition: unknown, fallback: string): string {
  const d = typeof disposition === 'string' ? disposition : '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(d);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="([^"]+)"/i.exec(d);
  return plain?.[1] || fallback;
}

/**
 * GET /statements/:id/pdf and save it. A final statement's PDF is stored; a draft's is
 * rendered on demand (the report engine; up to a minute or two), hence the long timeout.
 * An error body (JSON inside the blob) is turned back into an axios-like error.
 */
export async function downloadStatementPdf(id: string, fallbackName = 'usage-statement.pdf'): Promise<string> {
  let res;
  try {
    res = await api.get(`${BASE}/statements/${enc(id)}/pdf`, { responseType: 'blob', timeout: 180000 });
  } catch (err) {
    const e = err as { response?: { data?: unknown } };
    const data = e?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text()) as Record<string, unknown>;
        (e.response as { data?: unknown }).data = parsed;
      } catch {
        /* not JSON */
      }
    }
    throw err;
  }
  const blob = res.data as Blob;
  const name = filenameOf(res.headers?.['content-disposition'], fallbackName);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
  return name;
}

/** The API's error text, else `fallback` (403 has its own words). */
export function billingErrorText(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string } | null;
  const msg = e?.response?.data?.error || e?.response?.data?.message;
  if (msg) return msg;
  if (e?.response?.status === 403) return 'You do not have permission for this.';
  return fallback;
}

/** The API's error code (`month_open`, `already_final`, `stale_version` …), else null. */
export function billingErrorCode(err: unknown): string | null {
  const e = err as { response?: { data?: { code?: string } } } | null;
  return e?.response?.data?.code ?? null;
}
